<template>
  <section class="pricing" id="pricing">
    <div class="pricing__title-wrapper">
      <h3 class="pricing__title">Cart</h3>
    </div>
    <div class="pricing__breakdown">
      <div v-if="subscriptionsList.length" class="pricing__products-section">
        <PricingListItem
          v-for="product in subscriptionsList"
          :productType="productTypes.SUBSCRIPTION"
          :key="product.id"
          :product="product"
        />
      </div>
      <div
        v-if="certificationsList.length"
        class="pricing__products-section pricing__products-section--certifications"
      >
        <PricingListItem
          v-for="product in certificationsList"
          :productType="productTypes.CERTIFICATION"
          :key="product.id"
          :product="product"
        />
      </div>
      <div
        v-if="liveVirtualTrainingsInCart.length"
        class="pricing__products-section pricing__products-section--live-virtual-trainings"
      >
        <PricingListItem
          v-for="product in liveVirtualTrainingsInCart"
          :productType="productTypes.LVT"
          :key="product.id"
          :product="product"
        />
      </div>
      <div
        v-if="productBundleInCart"
        class="pricing__products-section pricing__products-section--product-bundles"
      >
        <PricingListItemProductBundle
          :productType="productTypes.BUNDLE"
          :key="productBundleInCart.id"
          :product="productBundleInCart"
          @bundle-certification-selected="bundleCertificationSelectedAction"
        />
      </div>
      <AddOns :bundleCertificationSelected="bundleCertificationSelected" />
      <div class="pricing__prices-wrapper">
        <PricingListPromoCode />
        <PricingListPriceDetails />
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { updateCart, promoProductBundleUtils } from '@/helpers/mixins'
import PricingListItem from '@/components/cart/pricing-list/PricingListItem.vue'
import PricingListItemProductBundle from '@/components/cart/pricing-list/PricingListItemProductBundle.vue'
import PricingListPriceDetails from '@/components/cart/pricing-list/PricingListPriceDetails.vue'
import PricingListPromoCode from '@/components/cart/pricing-list/PricingListPromoCode.vue'
import AddOns from '@/components/cart/add-ons/AddOns.vue'
import CONSTANTS from '@/constants'

const { PRODUCT_TYPES } = CONSTANTS

export default {
  name: 'PricingList',
  mixins: [updateCart, promoProductBundleUtils],
  components: {
    PricingListItem,
    PricingListItemProductBundle,
    PricingListPriceDetails,
    PricingListPromoCode,
    AddOns,
  },
  data() {
    return {
      certificationsInAddOns: [],
      subscriptionsList: [],
      bundleCertificationSelected: null,
      productTypes: PRODUCT_TYPES,
    }
  },
  computed: {
    ...mapState({
      availableLiveVirtualTrainings: state => state.products.availableLiveVirtualTrainings,
      availableProductBundles: state => state.products.availableProductBundles,
    }),
    ...mapGetters({
      isBusinessUser: 'business/isBusinessUser',
      availablePlans: 'products/availablePlans',
      recurlyPricing: 'recurly/getRecurlyPricing',
      selectedPlanCode: 'shoppingCart/selectedPlanCode',
      availableCertifications: 'products/availableCertifications',
    }),
    subscriptionsInCart() {
      const codes = this.availablePlans.map(p => p.merchant_id)
      return this.shoppingCart.filter(i => codes.includes(i.merchant_id))
    },
    certificationsList() {
      const codes = this.availableCertifications.map(p => p.merchant_id)
      return this.shoppingCart.filter(i => codes.includes(i.merchant_id) && !i.addOn)
    },
    liveVirtualTrainingsInCart() {
      const codes = this.availableLiveVirtualTrainings.map(p => p.merchant_id)
      return this.shoppingCart.filter(i => codes.includes(i.merchant_id) && !i.addOn)
    },
    productBundleInCart() {
      const codes = this.availableProductBundles.map(p => p.slug)
      return this.shoppingCart.find(i => codes.includes(i.merchant_id) && !i.addOn)
    },
  },
  watch: {
    paymentEntries: {
      deep: true,
      handler(paymentEntries) {
        const { country, zip } = paymentEntries
        this.recurlyPricing
          .address({
            country,
            postal_code: zip,
          })
          .catch(err => this.handleError(err))
          .done()
      },
    },
    subscriptionsInCart() {
      this.setSubscriptionsList()
    },
  },
  created() {
    this.skillDiveItemReset()
    this.shoppingCart.forEach(item => {
      if (item && Object.keys(item).includes('recurlyItem')) item.recurlyItem = false
    })
    this.setSubscriptionsList()
  },
  methods: {
    setSubscriptionsList() {
      const { PLANS } = CONSTANTS
      const codes = this.availablePlans.map(p => p.merchant_id)
      const subscriptionsList = []
      this.shoppingCart.forEach(i => {
        if (codes.includes(i.merchant_id) && i.merchant_id === PLANS.SKILL_DIVE_ANNUAL) {
          if (!i.selectedAsAddOns) subscriptionsList.push(i)
        } else if (codes.includes(i.merchant_id)) {
          subscriptionsList.push(i)
        }
      })
      this.subscriptionsList = subscriptionsList
    },
    skillDiveItemReset() {
      const plan = this.shoppingCart.find(
        item => item.merchant_id === CONSTANTS.PLANS.SKILL_DIVE_ANNUAL
      )
      if (plan && Object.keys(plan).includes('asAddOns')) plan.asAddOns = false
      if (plan && Object.keys(plan).includes('recurlySDLACounter')) plan.recurlySDLACounter = 0
      if (plan && Object.keys(plan).includes('recurlySDItem')) plan.recurlySDItem = false
    },
    bundleCertificationSelectedAction(voucher) {
      this.bundleCertificationSelected = voucher
      const isVoucherOnCart = this.shoppingCart.find(i => i.merchant_id === voucher.voucher)
      if (isVoucherOnCart) {
        this.removeProductFromCart(isVoucherOnCart)
      }
    },
  },
}
</script>

<style lang="scss">
.pricing {
  height: 100%;
  width: 100%;
  margin: 0 auto;
  &__title-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 0 20px 0;
  }
  &__title {
    color: var(--white);
    font-family: var(--font-regular);
    font-weight: 450;
    font-size: rem(24);
    line-height: 32px;
    margin: 0;
  }
  &__count-badge {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: flex-start;
    padding: 9px 14px;
    background: linear-gradient(
      111.02deg,
      rgba(18, 18, 18, 0.7) 3.86%,
      rgba(18, 18, 18, 0) 107.96%
    );
    border-radius: 20px;
    text-transform: uppercase;
    font-size: rem(12);
    line-height: 14px;
    font-family: var(--font-regular);
  }
  &__breakdown {
    padding: 0;
  }
  &__prices-wrapper {
    position: relative;
    margin: 20px 0 0 0;
    @include tablet {
      margin: 50px 0 0 0;
      margin: 0;
    }
  }
  &__input {
    background: #fff;
    padding: 5px;
    margin: 0;
    width: 100%;
    border-radius: 5px;
    font-size: 1rem;
    border: 1px solid var(--faded-gray);
    opacity: 0.7;
    font-family: var(--font-regular);
    &:focus {
      opacity: 1;
    }
  }
  &__products {
    &-section {
      display: flex;
      flex-flow: column;
      row-gap: 10px;
      &--certifications,
      &--live-virtual-trainings,
      &--product-bundles {
        margin-top: 10px;
        .pricing-list-item {
          &__wrapper {
            height: auto;
            align-items: center;
          }
          &__btn {
            align-self: auto;
          }
        }
      }
    }
  }
}
</style>
